import React, { FC, useEffect } from 'react'
import InviteModal from './InviateModal';
import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useDispatch } from 'react-redux';
import * as actions from "../../../store/addTenantUser/actions";

import { AlertTriangle, UserPlus } from 'lucide-react';

interface InviateMemberModalProps {
  showInviteModal: boolean;
  onClose: () => void;
  loading: boolean;
  tenantProducts: any;
  selectedProducts: string[];
  tenantid: string;
  testProducts: any[];
  userList: any[];
  formData: {
    email: string;
    name: string;
    surname: string;
    products?: string[];
  };
  setFormData: (value: any) => void;
  onAddClick?: () => void;
}

const InviateMemberModal: FC<InviateMemberModalProps> = ({
  showInviteModal,
  onClose,
  loading,

  tenantid,
  testProducts,
  onAddClick,
  formData,
  setFormData,
  tenantProducts,
  userList,
}) => {
  const [innerLoading, setInnerLoading] = useState(loading);
  const dispatch = useDispatch();
  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);
  const [alreadyExist, setAlreadyExist] = useState(false);
  const warningString = "It is not possible to update because ";
  const [messagesWarning, setMessagesWarning] = useState<string[]>([]);

  const [emailError, setEmailError] = useState('');

  const emailChange = (data: React.ChangeEvent<HTMLInputElement>) => {
    setAlreadyExist(false);
    setEmailError('');
    const email = data.target.value?.trim();
    const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;

    setFormData({
      ...formData,
      email: email,
    });

    if (!emailRegex.test(email)) {
      setEmailError('Insert a valid email address');
      return;
    }
    const exist = userList.find((user: any) => user.email === email);
    if (exist) {
      setAlreadyExist(true);
      setEmailError('This email is already present in the tenant');
      return;
    }


  };

  const handleInputChange = (data: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [data.target.name]: data.target.value,
    });
  };

  const handleProductChange = (productId: string) => {
    setSelectedProducts((prevSelected: string[]) => {
      const updatedSelected = prevSelected.includes(productId)
        ? prevSelected.filter((id) => id !== productId) // Deselect
        : [...prevSelected, productId]; // Select
      setSelectedProducts(updatedSelected);
      return updatedSelected;
    });
  };

  // Email validation with duplicated check if not already exist

  useEffect(() => {
    const regex = /[a-z0-9.-_]+@[a-z0-9.-]+\.[a-z]{2,3}/;
    if (!regex.test(formData.email)) {
      setAlreadyExist(true);
    }

  }, [formData.email]);



  const sendData = () => {
    const messages = [];
    if (alreadyExist) {
      messages.push(warningString + "the email is already present in the tenant");
    }
    if (emailError) {
      messages.push(warningString + emailError);
    }
    if (formData.name === '') {
      messages.push(warningString + "the name is required");
    }
    if (formData.surname === '') {
      messages.push(warningString + "the surname is required");
    }
    if (selectedProducts.length === 0) {
      messages.push(warningString + "it is necessary to select at least one product");
    }
    if (messages.length > 0) {
      setMessagesWarning(messages);
      return;
    }
    //console.log(" send data t prod", selectedProducts);
    const payload = {
      tenantId: tenantid,
      userEmail: formData.email,
      products: tenantProducts,
      testProducts: selectedProducts,
      name: formData.name,
      surname: formData.surname,
    };
    //console.log("payload", payload);
    dispatch(actions.postAddTenantUserRequest(payload));
    onAddClick?.();

  };


  return (
    <InviteModal
      isOpen={showInviteModal}
      onClose={onClose}
      icon={<UserPlus size={20} />}
      size="lg"
      title="Invite New Member"
      footerChildren={
        <>
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => {
              sendData();
            }}
            className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Add Member
          </motion.button>
        </>
      }
    >

      {/* Form Fields */}
      <div className="space-y-4 min-w-[450px] flex flex-col">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Email*
          </label>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={emailChange}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${emailError ? 'border-red-500 bg-red-50' : 'border-gray-300'
              }`}
            placeholder="email@example.com"
          />
          {emailError && (
            <motion.p
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="text-red-500 text-sm mt-1"
            >
              {emailError}
            </motion.p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Name*
          </label>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            placeholder="Name"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Surname*
          </label>
          <input
            type="text"
            name="surname"
            value={formData.surname}
            onChange={handleInputChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            placeholder="Surname"
          />
        </div>

        {/* Product Selection */}
        <div className="space-y-3">
          <h5 className="font-medium text-gray-700">Select Products*</h5>
          <div className="space-y-2 max-h-40 overflow-y-auto">
            {testProducts
              ?.filter((p) => p.tier === "Enterprise")
              .map((product) => (
                <label
                  key={product.productAppName}
                  className="flex items-center space-x-3 p-2 bg-[#f5f5f5] hover:bg-gray-50 rounded-lg cursor-pointer"
                >
                  <input
                    type="checkbox"
                    name="products"
                    checked={selectedProducts.includes(
                      product.productAppName
                    )}
                    onChange={() => {
                      handleProductChange(product.productAppName);
                      setFormData({
                        ...formData,
                        products: selectedProducts as any,
                      });
                    }}
                    className="w-4 h-4 text-[#0c1d46] border-gray-300 rounded focus:ring-[#0c1d46]"
                  />
                  <span className="text-gray-700">
                    {product.productAppName}
                  </span>
                </label>
              ))}
          </div>
        </div>

        {/* Error Message */}
        <AnimatePresence>
          {messagesWarning.length > 0 && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="text-red-500 text-sm bg-red-50 p-3 rounded-lg"
            >
              {messagesWarning.map((message) => (
                <p key={message} className="flex items-center gap-2"><AlertTriangle className="text-red-500 mr-1" size={16} /> {message}</p>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

    </InviteModal>
  )
}

export default InviateMemberModal